import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { TAB_KEYS } from 'pages/TasksDashboard/constants';

import s from './TasksDashboard.module.scss';

import type {
  TabKey,
  IDashboardTasksResponse,
} from 'pages/TasksDashboard/types';

interface ITasksTabsProps {
  activeTab: TabKey;
  onTabChange: (tabKey: TabKey) => void;
  data?: IDashboardTasksResponse;
  tabCounts?: Record<TabKey, number | undefined>;
}

const TasksTabs: FC<ITasksTabsProps> = ({
  activeTab,
  onTabChange,
  data,
  tabCounts,
}) => {
  const { t } = useTranslation();

  const tabItems = useMemo(() => {
    const getCount = (key: TabKey) => {
      if (tabCounts?.[key] != null) return ` (${tabCounts[key]})`;
      if (activeTab === key && data) return ` (${data.total})`;
      return '';
    };

    return [
      {
        key: TAB_KEYS.OPEN_POOL,
        label: `${t('tasksDashboard.tabs.openPool')}${getCount(
          TAB_KEYS.OPEN_POOL,
        )}`,
        id: 'dashboard-tab-open-pool',
      },
      {
        key: TAB_KEYS.ASSIGNED_TO_ME,
        label: `${t('tasksDashboard.tabs.assignToMe')}${getCount(
          TAB_KEYS.ASSIGNED_TO_ME,
        )}`,
        id: 'dashboard-tab-assigned-to-me',
      },
      {
        key: TAB_KEYS.COMPLETED,
        label: `${t('tasksDashboard.tabs.completed')}${getCount(
          TAB_KEYS.COMPLETED,
        )}`,
        id: 'dashboard-tab-completed',
      },
    ];
  }, [t, activeTab, data, tabCounts]);

  return (
    <div
      className={s['tab-container']}
      role="tablist"
      aria-label={t('tasksDashboard.tabs.ariaLabel')}
    >
      {tabItems.map((tab) => (
        <Button
          key={tab.key}
          id={tab.id}
          type={activeTab === tab.key ? 'primary' : 'default'}
          className={`${s['tab-button']} ${
            activeTab === tab.key ? s['filter-button-active'] : ''
          }`.trim()}
          onClick={() => onTabChange(tab.key)}
          size="small"
          role="tab"
          aria-selected={activeTab === tab.key}
          data-cy={`dashboard-${tab.key}-tab`}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );
};

export default TasksTabs;
